import { IFilterConfigItem } from '@/bundles/GroupFilters/interfaces/IFilterConfigItem';
import { FilterCategoryEnum, FilterTypeEnum } from '@/bundles/GroupFilters/enums/FilterTypeEnum';
import { IFilterButtonGroupItems } from '@/bundles/GroupFilters/interfaces/inputs/IFilterButtonGroupItems';
import { FilterOfficeEnterpriseItems } from '@/bundles/GroupFilters/enums/FilterOfficeEnterpriseEnum';
import { getIndustryTypes, getIndustryTypesByGroup } from '@/bundles/Companies/types/companyIndustryType';
import { Nullable } from '@/utils/types';
import { formatToSF } from '@/bundles/GroupFilters/helpers/formatters/formatToSF';
import {
  FilterPrimaryUseClassItems,
  getCountriesForFilters,
  getPropertyUsesForFilters
} from '@/bundles/GroupFilters/consts';
import { getRailsStatusItems } from '@/bundles/Property/helpers';
import { getOccupancyTypeFilterItems } from '@/bundles/Occupier/helpers/getOccupancyTypeFilterItems';
import { FilterBrokerageItems } from '@/bundles/GroupFilters/enums/FilterBrokerageEnum';
import { CompanySpatialSearchRecordTypeEnum, FilterPrimaryUseClassEnum } from '@/bundles/GroupFilters/types';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';
import { getShowArchiveFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getShowArchiveFilterConfig';
import { getStaticSetFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getStaticSetFilterConfig';

export const getCompanyFilters = (): IFilterConfigItem[] => {
  return [
    // FilterCategoryEnum.company_details start
    {
      title: 'Company Type',
      name: 'company_my_office_enterprise',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterOfficeEnterpriseItems,
      category: FilterCategoryEnum.company_details,
      additional: {
        mandatory: false,
      }
    },
    {
      title: 'Company Name',
      name: 'company_name',
      type: FilterTypeEnum.company_name_combobox,
      category: FilterCategoryEnum.company_details,
    },
    {
      title: 'City',
      name: 'company_city',
      type: FilterTypeEnum.city_autocomplete,
      category: FilterCategoryEnum.company_details,
      additional: {
        citiesSource: 'company',
      }
    },
    {
      title: 'State',
      name: 'company_state',
      type: FilterTypeEnum.states_autocomplete,
      category: FilterCategoryEnum.company_details,
    },
    {
      title: 'Country',
      name: 'company_country',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getCountriesForFilters(),
      category: FilterCategoryEnum.company_details,
    },
    {
      title: 'Postal',
      name: 'company_postal',
      type: FilterTypeEnum.postal_code_combobox,
      category: FilterCategoryEnum.company_details,
    },
    {
      title: 'Industry Group',
      name: 'company_industry_group',
      items: getIndustryTypes(),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_details,
    },
    {
      title: 'Industry',
      name: 'company_industry',
      items: getIndustryTypesByGroup(),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_details,
    },
    {
      title: 'Annual Revenue',
      name: 'company_annual_revenue',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.company_details,
    },
    {
      title: 'Is Brokerage',
      name: 'company_is_brokerage',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterBrokerageItems,
      category: FilterCategoryEnum.company_details,
      additional: {
        mandatory: false
      }
    },
    {
      title: i18n.t(LocalizationEnum.legacy_id) as string,
      name: 'company_legacy_id',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.company_details,
    },
    // FilterCategoryEnum.company_details end
    // FilterCategoryEnum.occupier_filters start
    {
      title: 'Occupier Size',
      name: 'occupier_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.occupier_filters,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Occupancy Type',
      name: 'occupier_occupancy_type',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getOccupancyTypeFilterItems(),
      category: FilterCategoryEnum.occupier_filters,
    },
    // FilterCategoryEnum.occupier_filters end
    // FilterCategoryEnum.property_filters start
    {
      title: 'Property Size',
      name: 'property_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_filters,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Property Land Size',
      name: 'property_land_size',
      type: FilterTypeEnum.land_size_range,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property Use',
      type: FilterTypeEnum.property_use_group,
      category: FilterCategoryEnum.property_filters,
      children: [
        {
          title: 'Property Use Class',
          name: 'property_use_class',
          type: FilterTypeEnum.button_group,
          items: FilterPrimaryUseClassItems,
          defaultValue: FilterPrimaryUseClassEnum.either,
        },
        {
          title: 'Property Use',
          name: 'property_primary_use',
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: getPropertyUsesForFilters(),
        },
      ],
    },
    {
      title: 'Property Use Type(s)',
      name: 'property_use_type',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.property_filters,
    },
    {
      title: 'Property Rail Status',
      name: 'property_rail_status',
      items: getRailsStatusItems(),
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.property_filters,
    },
    // FilterCategoryEnum.property_filters end
    // FilterCategoryEnum.polygon_search start
    {
      title: 'Search by',
      name: 'company_spatial_search_record_type',
      type: FilterTypeEnum.one_select,
      items: [
        { text: 'Company Location', value: CompanySpatialSearchRecordTypeEnum.company },
        { text: 'Property Location', value: CompanySpatialSearchRecordTypeEnum.property },
      ],
      category: FilterCategoryEnum.polygon_search,
    },
    // FilterCategoryEnum.polygon_search end
    // FilterCategoryEnum.created_edited start
    {
      title: 'Created/Edited Date',
      name: 'company_view_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created Date',
      name: 'company_created_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited Date',
      name: 'company_edited_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created/Edited by',
      name: 'company_view',
      type: FilterTypeEnum.create_updated_by_select, // should be handled in mappers to old filters
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created by',
      name: 'company_created_by',
      type: FilterTypeEnum.create_updated_by_select, // should be handled in mappers to old filters
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited by',
      name: 'company_edited_by',
      type: FilterTypeEnum.create_updated_by_select, // should be handled in mappers to old filters
      category: FilterCategoryEnum.created_edited,
    },
    // FilterCategoryEnum.created_edited end
    getShowArchiveFilterConfig(),
    getStaticSetFilterConfig('company'),
  ].filter(Boolean) as IFilterConfigItem[]; // filter out null values
};
